import { sortBy } from "lodash";
import { OpenTimeSeries, ValueType } from "@captor/openseries-ts";

import { DateHelper, TimeSeries } from "../../../../common/src";
import { InstrumentModelTypeEnum, PartyAccount, PartyAccountType } from "../../types.generated";
import { excludeInAttribution } from "../performance/functions";
import { ProcessedData, SelectedRowType } from "../performance/interfaces";

/** One year before `endDate`, not earlier than the client's first trade date. */
export function defaultOneYearStartDate(endDate: string, firstTradeDate?: string): string {
    if (!endDate) return firstTradeDate || "";
    const oneYearStart = DateHelper.addYears(endDate, -1);
    if (firstTradeDate && oneYearStart < firstTradeDate) return firstTradeDate;
    return oneYearStart;
}

export function accountIdsEqual(a: unknown, b: unknown): boolean {
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

export function getSelectableAccounts(accounts: Partial<PartyAccount>[] | undefined): Partial<PartyAccount>[] {
    if (!accounts || !accounts.length) return [];
    const result: Partial<PartyAccount>[] = [];
    for (const account of accounts) {
        if (account.type === PartyAccountType.Physical || account.type === PartyAccountType.Sum) {
            result.push(account);
        }
    }
    return sortBy(result, (account) => (account.description || account.name || "").toLowerCase());
}

export function accountOptionLabel(account: Partial<PartyAccount>): string {
    const name = account.description || account.name || account._id;
    const typeLabel = account.type === PartyAccountType.Physical ? "Physical" : "Sum";
    return `${name} (${typeLabel})`;
}

export function getDirectChildAccounts(selected: Partial<PartyAccount>, accounts: Partial<PartyAccount>[]): Partial<PartyAccount>[] {
    if (!selected || !accounts) return [];
    const childIds = (selected.childrenAccounts || []).map((child) => child._id).filter(Boolean);
    let children: Partial<PartyAccount>[];
    if (childIds.length) {
        const idSet = new Set(childIds);
        children = accounts.filter((account) => idSet.has(account._id));
    } else {
        children = accounts.filter((account) => account.parentAccountId === selected._id);
    }
    return getSelectableAccounts(children);
}

function toFiniteNumber(value: unknown): number | null {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value !== "") {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/**
 * Same compounding as GraphQL `InstrumentPerformance.series` when a start date is given:
 * the first day contributes no P&L, so a purchase cashflow does not look like a return.
 */
export function calculateSeriesFromStart(values: number[], cashFlows: number[]): number[] {
    const valuesLength = values.length;
    if (valuesLength === 0) return [];
    if (valuesLength === 1) return [1];

    const series = [1];
    const firstValue = values[0];
    const firstCashFlow = cashFlows[1] || 0;
    const totalReturn = values[1] - firstValue - firstCashFlow;
    const firstFactor = firstValue === 0 ? 1 : 1 + totalReturn / firstValue;
    series.push(series[0] * firstFactor);

    for (let i = 2; i < valuesLength; i++) {
        const periodReturn = values[i] - values[i - 1] - (cashFlows[i] || 0);
        const factor = values[i - 1] === 0 ? 1 : 1 + periodReturn / values[i - 1];
        series.push(series[i - 1] * factor);
    }
    return series;
}

/** First date in the window to plot an instrument: first trade, else first non-zero valuation/position. */
export function instrumentPerformanceStartDate(row: any, dateSeries: string[], startDate: string, endDate: string): string {
    let start = startDate || "";
    if (typeof row?.firstTradeDate === "string" && row.firstTradeDate > start) {
        start = row.firstTradeDate;
    }
    if (!Array.isArray(dateSeries) || !dateSeries.length) return start;

    let firstPositionDate: string | null = null;
    for (let i = 0; i < dateSeries.length; i++) {
        const date = dateSeries[i];
        if (date < start || (endDate && date > endDate)) continue;
        if (!firstPositionDate && isNonZeroQuantity(row?.positions?.[i])) {
            firstPositionDate = date;
        }
        const value = toFiniteNumber(row?.values?.[i]);
        if (value !== null && Math.abs(value) > 1e-6) {
            return date;
        }
    }
    return firstPositionDate || start;
}

export function seriesHasVariation(values: number[] | undefined, relTol = 1e-10): boolean {
    if (!Array.isArray(values) || values.length < 2) return false;
    const first = values[0];
    if (!Number.isFinite(first) || first === 0) {
        for (let i = 1; i < values.length; i++) {
            if (!Number.isFinite(values[i])) continue;
            if (Math.abs(values[i] - first) > relTol) return true;
        }
        return false;
    }
    for (let i = 1; i < values.length; i++) {
        if (!Number.isFinite(values[i])) continue;
        if (Math.abs(values[i] / first - 1) > relTol) return true;
    }
    return false;
}

function toIsoDateString(value: unknown): string {
    if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
    if (value instanceof Date && !Number.isNaN(value.getTime())) return DateHelper.dateToString(value);
    return "";
}

/** Sum of prices on the last valuation record — same reduction as the performance valuation pipeline. */
export function sumValuationRecordPrice(valuation: { records?: Array<{ prices?: Array<{ value?: number | string }> }> }): number | null {
    const records = valuation?.records;
    if (!Array.isArray(records) || records.length === 0) return null;
    const prices = records[records.length - 1]?.prices;
    if (!Array.isArray(prices) || prices.length === 0) return null;
    let sum = 0;
    let any = false;
    for (const price of prices) {
        const value = toFiniteNumber(price?.value);
        if (value === null) continue;
        sum += value;
        any = true;
    }
    return any ? sum : null;
}

export function groupValuationPrices(
    valuations: Array<{ instrumentId?: unknown; date?: unknown; records?: Array<{ prices?: Array<{ value?: number | string }> }> }>
): Record<string, { dates: string[]; values: number[] }> {
    const byId: Record<string, { dates: string[]; values: number[] }> = {};
    if (!Array.isArray(valuations)) return byId;
    for (const valuation of valuations) {
        const instrumentId = valuation?.instrumentId ? String(valuation.instrumentId) : "";
        const date = toIsoDateString(valuation?.date);
        const price = sumValuationRecordPrice(valuation);
        if (!instrumentId || !date || price === null) continue;
        if (!byId[instrumentId]) byId[instrumentId] = { dates: [], values: [] };
        byId[instrumentId].dates.push(date);
        byId[instrumentId].values.push(price);
    }
    for (const series of Object.values(byId)) {
        const paired = series.dates.map((date, i) => ({ date, value: series.values[i] }));
        paired.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
        series.dates = paired.map((item) => item.date);
        series.values = paired.map((item) => item.value);
    }
    return byId;
}

function firstPlottableVaryingSeries(name: string, dates: string[], values: number[]): OpenTimeSeries | null {
    if (!dates.length || dates.length !== values.length) return null;
    const ts = seriesToOpenTimeSeries(name, dates, values, dates[0], dates[dates.length - 1]);
    if (!ts) return null;
    if (!seriesHasVariation(ts.getTsdfValues())) return null;
    return ts;
}

export function seriesToOpenTimeSeries(
    name: string,
    dates: string[],
    values: number[],
    startDate: string,
    endDate: string,
    rebase = true
): OpenTimeSeries | null {
    if (!name || !Array.isArray(dates) || !Array.isArray(values) || dates.length === 0) return null;
    const slicedDates: string[] = [];
    const slicedValues: number[] = [];
    for (let i = 0; i < dates.length; i++) {
        if (dates[i] < startDate || dates[i] > endDate) continue;
        const value = toFiniteNumber(values[i]);
        if (value === null) continue;
        slicedDates.push(dates[i]);
        slicedValues.push(value);
    }
    if (!seriesIsPlottable(slicedValues)) return null;
    let outValues = slicedValues;
    if (rebase && slicedValues[0] !== 0) {
        outValues = slicedValues.map((value) => value / slicedValues[0]);
    }
    return OpenTimeSeries.fromArrays(name, slicedDates, outValues, { valuetype: ValueType.PRICE });
}

/**
 * Holding-period return index from valuations (values + cashFlows), same source as /performance.
 * If that index is flat, fall back to rebased market values, then daily valuation prices.
 */
export function instrumentToOpenTimeSeries(
    row: any,
    dateSeries: string[],
    startDate: string,
    endDate: string,
    valuationPrices?: { dates: string[]; values: number[] }
): OpenTimeSeries | null {
    if (!row || !Array.isArray(dateSeries) || dateSeries.length === 0) return null;
    const name = instrumentRowLabel(row);
    const heldStart = instrumentPerformanceStartDate(row, dateSeries, startDate, endDate);
    const slicedDates: string[] = [];
    const slicedValues: number[] = [];
    const slicedCashFlows: number[] = [];
    const slicedSeries: number[] = [];
    let anyNonZeroValue = false;
    for (let i = 0; i < dateSeries.length; i++) {
        const date = dateSeries[i];
        if (date < heldStart || date > endDate) continue;
        const value = toFiniteNumber(row.values ? row.values[i] : null);
        const cashFlow = toFiniteNumber(row.cashFlows ? row.cashFlows[i] : null);
        const seriesValue = toFiniteNumber(row.series ? row.series[i] : null);
        if (value === null && seriesValue === null) continue;
        slicedDates.push(date);
        slicedValues.push(value === null ? 0 : value);
        slicedCashFlows.push(cashFlow === null ? 0 : cashFlow);
        slicedSeries.push(seriesValue === null ? 1 : seriesValue);
        if (value !== null && Math.abs(value) > 1e-6) anyNonZeroValue = true;
    }

    if (anyNonZeroValue && slicedValues.length >= 2) {
        const fromValuations = firstPlottableVaryingSeries(
            name,
            slicedDates,
            calculateSeriesFromStart(slicedValues, slicedCashFlows)
        );
        if (fromValuations) return fromValuations;
        const fromMarketValues = firstPlottableVaryingSeries(name, slicedDates, slicedValues);
        if (fromMarketValues) return fromMarketValues;
    }
    const fromSeries = firstPlottableVaryingSeries(name, slicedDates, slicedSeries);
    if (fromSeries) return fromSeries;
    if (valuationPrices && valuationPrices.dates && valuationPrices.values) {
        const fromPrices = seriesToOpenTimeSeries(name, valuationPrices.dates, valuationPrices.values, heldStart, endDate);
        if (fromPrices && seriesHasVariation(fromPrices.getTsdfValues())) return fromPrices;
    }
    return seriesToOpenTimeSeries(name, slicedDates, slicedSeries, slicedDates[0] || heldStart, endDate);
}

export function getAccountSelectedRow(
    accountId: string,
    accountName: string,
    processedData: ProcessedData,
    startDate: string,
    endDate: string,
    includeModelIndex: boolean
): SelectedRowType | null {
    if (!processedData || !accountId) return null;
    const {
        dateSeries,
        accountSeriesById,
        accountValuesById,
        accountPositionsById,
        benchmarksById,
        showBenchmarksById,
        modelSeriesById,
        modelSeriesNameById,
        riskFreeSeries
    } = processedData;

    const accountSeries = accountSeriesById[accountId];
    if (!accountSeries || !dateSeries || !dateSeries.length) return null;

    const series: number[] = [];
    const values: number[] = [];
    const positionFlag: number[] = [];
    const modelSeries: number[] = [];
    for (let i = 0; i < dateSeries.length; i++) {
        series.push(accountSeries[i]);
        values.push(accountValuesById[accountId] ? accountValuesById[accountId][i] : 0);
        positionFlag.push(accountPositionsById[accountId] ? accountPositionsById[accountId][i] : 1);
        if (modelSeriesById[accountId]) modelSeries.push(modelSeriesById[accountId][i]);
    }

    const performance = new TimeSeries(dateSeries, series, accountName);
    const mainBenchmark = benchmarksById[accountId];
    const showBenchmarks = showBenchmarksById[accountId];
    const modelSeriesName = modelSeriesNameById[accountId] ? modelSeriesNameById[accountId] : "Model Weighted Index";

    const benchmarks: TimeSeries[] = [];
    if (mainBenchmark && mainBenchmark.series) {
        benchmarks.push(mainBenchmark.series.clone());
    }

    let modelBenchmarkName: string = undefined;
    if (includeModelIndex && Array.isArray(modelSeries) && modelSeries.length) {
        let hasNaN = false;
        for (let i = 0; i < modelSeries.length; i++) {
            if (isNaN(modelSeries[i])) {
                hasNaN = true;
                break;
            }
        }
        if (!hasNaN && (!mainBenchmark || !mainBenchmark.series || mainBenchmark.series.name !== modelSeriesName)) {
            benchmarks.push(new TimeSeries(dateSeries, modelSeries, modelSeriesName));
            modelBenchmarkName = modelSeriesName;
        }
    }

    if (Array.isArray(showBenchmarks) && showBenchmarks.length) {
        for (const showBenchmark of showBenchmarks) {
            if (mainBenchmark && showBenchmark.instrumentId === mainBenchmark.instrumentId) continue;
            if (showBenchmark.series) benchmarks.push(showBenchmark.series.clone());
        }
    }

    return {
        name: accountName,
        performance,
        values,
        instrumentPerformances: [],
        benchmarks,
        modelBenchmarkName,
        riskFree: riskFreeSeries ? riskFreeSeries.clone() : null,
        startDate,
        endDate,
        dateSeries,
        positionFlag
    };
}

function isNonZeroQuantity(quantity: unknown): boolean {
    return typeof quantity === "number" && Number.isFinite(quantity) && Math.abs(quantity) > 1e-6;
}

/**
 * True when the instrument is still held on `asOfDate`, using quantity only
 * (never market value, which is usually quantity × price).
 *
 * Prefer live trading-manager quantity. Otherwise use the last finite performance
 * quantity on or before `min(asOfDate, valuationDate)`.
 */
export function instrumentHasNonZeroPositionOnDate(row: any, dateSeries: string[], asOfDate: string): boolean {
    if (!row) return false;
    if (typeof row.tradingmanagerQuantity === "number" && Number.isFinite(row.tradingmanagerQuantity)) {
        return isNonZeroQuantity(row.tradingmanagerQuantity);
    }
    if (!Array.isArray(dateSeries) || !dateSeries.length || !asOfDate) return false;
    const quantities = Array.isArray(row.positions) ? row.positions : null;
    if (!quantities || !quantities.length) return false;

    let lastKnownDate = asOfDate;
    if (typeof row.valuationDate === "string" && row.valuationDate && row.valuationDate < asOfDate) {
        lastKnownDate = row.valuationDate;
    }

    for (let i = dateSeries.length - 1; i >= 0; i--) {
        if (dateSeries[i] > lastKnownDate) continue;
        if (i >= quantities.length) continue;
        const quantity = quantities[i];
        if (typeof quantity !== "number" || !Number.isFinite(quantity)) continue;
        return isNonZeroQuantity(quantity);
    }
    return false;
}

/** Enough finite, non-all-zero points to show in the line chart, bars, and metrics table. */
export function seriesIsPlottable(values: number[] | undefined): boolean {
    if (!Array.isArray(values) || values.length < 2) return false;
    let finite = 0;
    let anyNonZero = false;
    for (const value of values) {
        if (!Number.isFinite(value)) continue;
        finite += 1;
        if (value !== 0) anyNonZero = true;
    }
    return finite >= 2 && anyNonZero;
}

/** Group holdings on physical accounts when the position count exceeds this. */
export const INSTRUMENT_GROUPING_POSITION_THRESHOLD = 10;

export const INSTRUMENT_PERFORMANCE_OTHER_GROUP_NAME = "Other model types";
export const INSTRUMENT_CONTRIBUTION_OTHER_GROUP_NAME = "Other";

export const INSTRUMENT_PERFORMANCE_GROUPS: { name: string; modelTypes: InstrumentModelTypeEnum[] }[] = [
    { name: "CdsBasket & CdsIndex", modelTypes: [InstrumentModelTypeEnum.CdsBasket, InstrumentModelTypeEnum.CdsIndex] },
    {
        name: "Bond, Balance & FxSwap",
        modelTypes: [InstrumentModelTypeEnum.Bond, InstrumentModelTypeEnum.Balance, InstrumentModelTypeEnum.FxSwap]
    },
    { name: "Stock & PortfolioSwapX", modelTypes: [InstrumentModelTypeEnum.Stock, InstrumentModelTypeEnum.PortfolioSwapX] },
    { name: "Swap & Swaption", modelTypes: [InstrumentModelTypeEnum.Swap, InstrumentModelTypeEnum.Swaption] }
];

export const INSTRUMENT_PERFORMANCE_GROUP_ORDER: string[] = [
    ...INSTRUMENT_PERFORMANCE_GROUPS.map((group) => group.name),
    INSTRUMENT_PERFORMANCE_OTHER_GROUP_NAME
];

const MODEL_TYPE_TO_PERFORMANCE_GROUP: Record<string, string> = (() => {
    const map: Record<string, string> = {};
    for (const group of INSTRUMENT_PERFORMANCE_GROUPS) {
        for (const modelType of group.modelTypes) {
            map[modelType] = group.name;
        }
    }
    return map;
})();

export function instrumentPerformanceGroupName(modelType: string | undefined | null): string {
    if (!modelType) return INSTRUMENT_PERFORMANCE_OTHER_GROUP_NAME;
    return MODEL_TYPE_TO_PERFORMANCE_GROUP[modelType] || INSTRUMENT_PERFORMANCE_OTHER_GROUP_NAME;
}

export function getInstrumentRowsForAccount(
    accountId: string,
    processedData: ProcessedData,
    asOfDate?: string,
    options?: { includeExcluded?: boolean }
): any[] {
    if (!processedData || !processedData.dataWithLevels) return [];
    const includeExcluded = Boolean(options?.includeExcluded);
    const rows: any[] = [];
    for (const row of processedData.dataWithLevels) {
        if (!accountIdsEqual(row.accountId, accountId)) continue;
        if (
            !includeExcluded &&
            row.instrument &&
            row.instrument.modelType &&
            excludeInAttribution(row.instrument.modelType as InstrumentModelTypeEnum)
        ) {
            continue;
        }
        if (asOfDate && !instrumentHasNonZeroPositionOnDate(row, processedData.dateSeries, asOfDate)) {
            continue;
        }
        rows.push(row);
    }
    return sortBy(rows, (row) => (row.instrument?.longName || row.instrument?.name || "").toLowerCase());
}

/** Held rows (including types normally hidden in attribution) plus live holdings missing from performance. */
export function countHeldInstrumentPositions(accountId: string, processedData: ProcessedData, asOfDate?: string): number {
    const rows = getInstrumentRowsForAccount(accountId, processedData, asOfDate, { includeExcluded: true });
    return rows.length + getMissingHeldInstrumentIds(accountId, processedData).length;
}

export function shouldGroupInstrumentPerformance(
    account: Partial<PartyAccount> | null | undefined,
    processedData: ProcessedData | null | undefined,
    asOfDate: string,
    groupingEnabled: boolean,
    options?: { includeHistorical?: boolean }
): boolean {
    if (!groupingEnabled || !account || !processedData) return false;
    if (account.type !== PartyAccountType.Physical) return false;
    const heldAsOf = options?.includeHistorical ? undefined : asOfDate;
    return countHeldInstrumentPositions(account._id, processedData, heldAsOf) > INSTRUMENT_GROUPING_POSITION_THRESHOLD;
}

/** Sum member valuations into one row so grouped performance uses the same TWR path as a single instrument. */
export function combineInstrumentRowsForGroup(groupName: string, rows: any[], length: number): any {
    const values = new Array(length).fill(0);
    const cashFlows = new Array(length).fill(0);
    const positions = new Array(length).fill(0);
    let firstTradeDate: string | undefined;
    for (const row of rows) {
        if (typeof row?.firstTradeDate === "string" && (!firstTradeDate || row.firstTradeDate < firstTradeDate)) {
            firstTradeDate = row.firstTradeDate;
        }
        for (let i = 0; i < length; i++) {
            const value = toFiniteNumber(row?.values?.[i]);
            if (value !== null) values[i] += value;
            const cashFlow = toFiniteNumber(row?.cashFlows?.[i]);
            if (cashFlow !== null) cashFlows[i] += cashFlow;
            const position = toFiniteNumber(row?.positions?.[i]);
            if (position !== null) positions[i] += position;
        }
    }
    return {
        instrument: { name: groupName, longName: groupName },
        values,
        cashFlows,
        positions,
        firstTradeDate
    };
}

export type NamedInstrumentRows = { name: string; rows: any[] };

function previousTotalValue(accountValues: number[] | undefined, groups: NamedInstrumentRows[], prevIndex: number): number {
    const fromAccount = toFiniteNumber(accountValues?.[prevIndex]);
    if (fromAccount !== null && fromAccount !== 0) return fromAccount;
    let sum = 0;
    for (const group of groups) {
        for (const row of group.rows) {
            const value = toFiniteNumber(row?.values?.[prevIndex]);
            if (value !== null) sum += value;
        }
    }
    return sum;
}

function groupDeltaAt(rows: any[], prevIndex: number, index: number): number {
    let delta = 0;
    for (const row of rows) {
        const prevValue = toFiniteNumber(row?.values?.[prevIndex]) ?? 0;
        const currValue = toFiniteNumber(row?.values?.[index]) ?? 0;
        const flow = toFiniteNumber(row?.cashFlows?.[index]) ?? 0;
        delta += currValue - prevValue - flow;
    }
    return delta;
}

function carinoPeriodLink(returnValue: number): number {
    if (returnValue === 0 || returnValue <= -1) return 1;
    return Math.log1p(returnValue) / returnValue;
}

/**
 * Carino/Menchero-linked contribution as in py-utils attribution.py
 * `compute_grouped_attribution_with_cumulative(..., method="carino_menchero")`.
 * Daily contrib = (Δvalue − cashFlow) / previous total account value.
 * Returned as a 1 + cumulative index so percent-from-one charts start at 0%.
 */
export function computeCarinoContributionIndices(
    namedGroups: NamedInstrumentRows[],
    dateSeries: string[],
    accountValues: number[] | undefined,
    startDate: string,
    endDate: string
): OpenTimeSeries[] {
    const groups = (namedGroups || []).filter((group) => group && group.name && Array.isArray(group.rows) && group.rows.length);
    if (!groups.length || !Array.isArray(dateSeries) || !dateSeries.length) return [];

    const indices: number[] = [];
    for (let t = 0; t < dateSeries.length; t++) {
        if (dateSeries[t] < startDate || dateSeries[t] > endDate) continue;
        indices.push(t);
    }
    const n = indices.length;
    if (n < 2) return [];

    const daily: number[][] = groups.map(() => new Array(n).fill(0));
    const portDaily: number[] = new Array(n).fill(0);
    for (let k = 1; k < n; k++) {
        const index = indices[k];
        const prevIndex = indices[k - 1];
        const totalPrev = previousTotalValue(accountValues, groups, prevIndex);
        if (totalPrev === 0) continue;
        for (let g = 0; g < groups.length; g++) {
            daily[g][k] = groupDeltaAt(groups[g].rows, prevIndex, index) / totalPrev;
            portDaily[k] += daily[g][k];
        }
    }

    const dates = indices.map((index) => dateSeries[index]);
    const list: OpenTimeSeries[] = [];
    for (let g = 0; g < groups.length; g++) {
        const values: number[] = new Array(n).fill(1);
        for (let k = 1; k < n; k++) {
            let cumReturnFactor = 1;
            for (let i = 1; i <= k; i++) {
                cumReturnFactor *= 1 + portDaily[i];
            }
            const totalCumReturn = cumReturnFactor - 1;
            const totalLink = carinoPeriodLink(totalCumReturn);
            let linkedSum = 0;
            for (let tau = 1; tau <= k; tau++) {
                linkedSum += daily[g][tau] * (carinoPeriodLink(portDaily[tau]) / totalLink);
            }
            values[k] = 1 + linkedSum;
        }
        const ts = seriesToOpenTimeSeries(groups[g].name, dates, values, dates[0], dates[n - 1], false);
        if (ts) list.push(ts);
    }
    return list;
}

export function contributionWaterfallItems(
    series: OpenTimeSeries[],
    referenceName?: string
): { name: string; value: number }[] {
    const items: { name: string; value: number }[] = [];
    let total = 0;
    for (const ts of series || []) {
        const name = ts?.name || "";
        if (!name || (referenceName && name === referenceName)) continue;
        const values = ts.getTsdfValues ? ts.getTsdfValues() : [];
        if (!Array.isArray(values) || values.length < 2) continue;
        const first = values[0];
        const last = values[values.length - 1];
        if (!Number.isFinite(first) || !Number.isFinite(last)) continue;
        const value = first === 0 ? last : last - first;
        items.push({ name, value });
        total += value;
    }
    if (!items.length) return [];
    items.push({ name: "TOTAL", value: total });
    return items;
}

function rowsByPerformanceGroup(rows: any[]): Record<string, any[]> {
    const rowsByGroup: Record<string, any[]> = {};
    for (const row of rows) {
        const groupName = instrumentPerformanceGroupName(row.instrument?.modelType);
        if (!rowsByGroup[groupName]) rowsByGroup[groupName] = [];
        rowsByGroup[groupName].push(row);
    }
    return rowsByGroup;
}

function appendAccountSeries(
    list: OpenTimeSeries[],
    accountName: string,
    processedData: ProcessedData,
    accountId: string,
    startDate: string,
    endDate: string
): void {
    const accountTs = seriesToOpenTimeSeries(
        accountName,
        processedData.dateSeries,
        processedData.accountSeriesById[accountId],
        startDate,
        endDate
    );
    if (accountTs) list.push(accountTs);
}

export function instrumentRowLabel(row: any): string {
    if (!row || !row.instrument) return "Instrument";
    return row.instrument.longName || row.instrument.name || "Instrument";
}

export function getMissingHeldInstrumentIds(accountId: string, processedData: ProcessedData): string[] {
    if (!accountId || !processedData || !Array.isArray(processedData.heldPositions)) return [];
    const present = new Set<string>();
    for (const row of processedData.dataWithLevels || []) {
        if (!accountIdsEqual(row.accountId, accountId) || !row.instrumentId) continue;
        present.add(String(row.instrumentId));
    }
    const missing: string[] = [];
    for (const position of processedData.heldPositions) {
        if (!accountIdsEqual(position.accountId, accountId)) continue;
        if (!isNonZeroQuantity(position.quantity)) continue;
        if (!present.has(String(position.instrumentId))) missing.push(String(position.instrumentId));
    }
    return missing;
}

export function extraHeldSeriesFromPrices(
    accountId: string,
    processedData: ProcessedData,
    priceSeries: { instrumentId?: string; dates?: string[]; values?: Array<string | number>; instrument?: { name?: string; longName?: string } }[],
    startDate: string,
    endDate: string
): OpenTimeSeries[] {
    const missing = new Set(getMissingHeldInstrumentIds(accountId, processedData));
    if (!missing.size || !Array.isArray(priceSeries)) return [];
    const nameById: Record<string, string> = {};
    for (const position of processedData.heldPositions || []) {
        if (position.name) nameById[String(position.instrumentId)] = position.name;
    }
    const list: OpenTimeSeries[] = [];
    for (const series of priceSeries) {
        const instrumentId = series?.instrumentId ? String(series.instrumentId) : "";
        if (!instrumentId || !missing.has(instrumentId)) continue;
        const dates = series.dates || [];
        const values = (series.values || []).map((value) => (typeof value === "number" ? value : parseFloat(value)));
        const name = series.instrument?.longName || series.instrument?.name || nameById[instrumentId] || "Instrument";
        const ts = seriesToOpenTimeSeries(name, dates, values, startDate, endDate);
        if (ts) list.push(ts);
    }
    return sortBy(list, (ts) => (ts.name || "").toLowerCase());
}

export function extraHeldSeriesFromValuations(
    accountId: string,
    processedData: ProcessedData,
    valuationPricesById: Record<string, { dates: string[]; values: number[] }>,
    startDate: string,
    endDate: string
): OpenTimeSeries[] {
    const missing = getMissingHeldInstrumentIds(accountId, processedData);
    if (!missing.length || !valuationPricesById) return [];
    const nameById: Record<string, string> = {};
    for (const position of processedData.heldPositions || []) {
        if (position.name) nameById[String(position.instrumentId)] = position.name;
    }
    const list: OpenTimeSeries[] = [];
    for (const instrumentId of missing) {
        const prices = valuationPricesById[instrumentId];
        if (!prices) continue;
        const name = nameById[instrumentId] || "Instrument";
        const ts = seriesToOpenTimeSeries(name, prices.dates, prices.values, startDate, endDate);
        if (ts) list.push(ts);
    }
    return sortBy(list, (ts) => (ts.name || "").toLowerCase());
}

export function getInstrumentIdsForValuations(accountId: string, processedData: ProcessedData, asOfDate?: string): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();
    const add = (id: unknown) => {
        if (!id) return;
        const key = String(id);
        if (seen.has(key)) return;
        seen.add(key);
        ids.push(key);
    };
    for (const row of getInstrumentRowsForAccount(accountId, processedData, asOfDate)) {
        add(row.instrumentId);
    }
    for (const id of getMissingHeldInstrumentIds(accountId, processedData)) {
        add(id);
    }
    return ids;
}

export function buildVsIndicesSeries(selectedRow: SelectedRowType, startDate: string, endDate: string): OpenTimeSeries[] {
    const list: OpenTimeSeries[] = [];
    if (!selectedRow) return list;
    const accountTs = seriesToOpenTimeSeries(
        selectedRow.name,
        selectedRow.dateSeries,
        selectedRow.performance ? selectedRow.performance.__values : [],
        startDate,
        endDate
    );
    if (accountTs) list.push(accountTs);
    for (const benchmark of selectedRow.benchmarks || []) {
        const ts = seriesToOpenTimeSeries(benchmark.name, selectedRow.dateSeries, benchmark.__values, startDate, endDate);
        if (ts) list.push(ts);
    }
    return list;
}

function buildGroupedConstituentSeries(
    account: Partial<PartyAccount>,
    accountName: string,
    processedData: ProcessedData,
    startDate: string,
    endDate: string,
    accountContribution: boolean
): OpenTimeSeries[] {
    const list: OpenTimeSeries[] = [];
    const dateSeries = processedData.dateSeries || [];
    const heldAsOf = accountContribution ? undefined : endDate;
    const rows = getInstrumentRowsForAccount(account._id, processedData, heldAsOf, { includeExcluded: true });
    const rowsByGroup = rowsByPerformanceGroup(rows);
    const accountValues = processedData.accountValuesById ? processedData.accountValuesById[account._id] : undefined;
    if (accountContribution) {
        const namedGroups: NamedInstrumentRows[] = [];
        for (const groupName of INSTRUMENT_PERFORMANCE_GROUP_ORDER) {
            const groupRows = rowsByGroup[groupName];
            if (!groupRows || !groupRows.length) continue;
            namedGroups.push({
                name: groupName === INSTRUMENT_PERFORMANCE_OTHER_GROUP_NAME ? INSTRUMENT_CONTRIBUTION_OTHER_GROUP_NAME : groupName,
                rows: groupRows
            });
        }
        for (const ts of computeCarinoContributionIndices(namedGroups, dateSeries, accountValues, startDate, endDate)) {
            list.push(ts);
        }
    } else {
        for (const groupName of INSTRUMENT_PERFORMANCE_GROUP_ORDER) {
            const groupRows = rowsByGroup[groupName];
            if (!groupRows || !groupRows.length) continue;
            const combined = combineInstrumentRowsForGroup(groupName, groupRows, dateSeries.length);
            const ts = instrumentToOpenTimeSeries(combined, dateSeries, startDate, endDate);
            if (ts) list.push(ts);
        }
    }
    appendAccountSeries(list, accountName, processedData, account._id, startDate, endDate);
    return list;
}

function buildPhysicalConstituentSeries(
    account: Partial<PartyAccount>,
    accountName: string,
    processedData: ProcessedData,
    startDate: string,
    endDate: string,
    extraSeries: OpenTimeSeries[],
    valuationPricesById: Record<string, { dates: string[]; values: number[] }>,
    accountContribution: boolean
): OpenTimeSeries[] {
    const list: OpenTimeSeries[] = [];
    if (accountContribution) {
        const rows = getInstrumentRowsForAccount(account._id, processedData, undefined, { includeExcluded: true });
        const namedGroups: NamedInstrumentRows[] = rows.map((row) => ({ name: instrumentRowLabel(row), rows: [row] }));
        const accountValues = processedData.accountValuesById ? processedData.accountValuesById[account._id] : undefined;
        for (const ts of computeCarinoContributionIndices(namedGroups, processedData.dateSeries, accountValues, startDate, endDate)) {
            list.push(ts);
        }
        appendAccountSeries(list, accountName, processedData, account._id, startDate, endDate);
        return list;
    }
    const rows = getInstrumentRowsForAccount(account._id, processedData, endDate);
    for (const row of rows) {
        const valuationPrices = row.instrumentId ? valuationPricesById[String(row.instrumentId)] : undefined;
        const ts = instrumentToOpenTimeSeries(row, processedData.dateSeries, startDate, endDate, valuationPrices);
        if (ts) list.push(ts);
    }
    for (const ts of extraSeries) {
        if (ts) list.push(ts);
    }
    appendAccountSeries(list, accountName, processedData, account._id, startDate, endDate);
    return list;
}

export function buildConstituentSeries(
    account: Partial<PartyAccount>,
    accountName: string,
    processedData: ProcessedData,
    startDate: string,
    endDate: string,
    extraSeries: OpenTimeSeries[] = [],
    valuationPricesById: Record<string, { dates: string[]; values: number[] }> = {},
    groupInstruments = false,
    accountContribution = false
): OpenTimeSeries[] {
    const list: OpenTimeSeries[] = [];
    if (!account || !processedData) return list;
    if (
        shouldGroupInstrumentPerformance(account, processedData, endDate, groupInstruments, {
            includeHistorical: accountContribution
        })
    ) {
        return buildGroupedConstituentSeries(account, accountName, processedData, startDate, endDate, accountContribution);
    }
    if (account.type === PartyAccountType.Physical) {
        return buildPhysicalConstituentSeries(
            account,
            accountName,
            processedData,
            startDate,
            endDate,
            extraSeries,
            valuationPricesById,
            accountContribution
        );
    }
    const children = getDirectChildAccounts(account, Object.values(processedData.accountsById || {}));
    for (const child of children) {
        const childName = child.description || child.name || child._id;
        const childSeries = processedData.accountSeriesById[child._id];
        const ts = seriesToOpenTimeSeries(childName, processedData.dateSeries, childSeries, startDate, endDate);
        if (ts) list.push(ts);
    }
    appendAccountSeries(list, accountName, processedData, account._id, startDate, endDate);
    return list;
}
